import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

// 定义尺寸对象的类型
interface Dimensions {
  width: number;
  height: number;
}

// 定义Hook的配置选项类型
interface UseContainerResizeOptions {
  // 是否在控制台打印变化日志（调试用）
  debug?: boolean;
  // 防抖延迟时间（毫秒），避免频繁更新
  debounceDelay?: number;
  // 尺寸变化时的回调函数
  onResize?: (dimensions: Dimensions) => void;
  // 是否在挂载时立即计算初始尺寸
  immediate?: boolean;
}

/**
 * 监控容器尺寸变化的自定义Hook
 * @param options 配置选项
 * @returns 返回容器引用和当前尺寸
 */
export const useContainerResize = (options: UseContainerResizeOptions = {}) => {

  // 状态：保存容器的当前尺寸
  const [dimensions, setDimensions] = useState<Dimensions>({ width: 0, height: 0 });

  // 引用：指向要监控的DOM元素
  const containerRef = useRef<HTMLDivElement>(null);
  // 引用：保存ResizeObserver实例
  const observerRef = useRef<ResizeObserver | null>(null);
  // 引用：保存防抖定时器
  const resizeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 使用 ref 保存回调函数和配置，避免重新创建 handleResize
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // 立即获取容器尺寸的函数
  const getImmediateDimensions = useCallback(() => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height
    };
  }, []);

  // 处理尺寸变化的回调函数
  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    if (!entries.length) return;

    const entry = entries[0];
    const { width, height } = entry.contentRect;

    if (optionsRef.current.debug) {
      console.log('容器尺寸变化:', { width, height });
    }

    // 清除之前的定时器
    if (resizeTimerRef.current) {
      clearTimeout(resizeTimerRef.current);
    }

    // 设置防抖定时器
    resizeTimerRef.current = setTimeout(() => {
      setDimensions(prev => {
        // 检查尺寸是否实际发生变化
        if (prev.width !== width || prev.height !== height) {
          const newDimensions = { width, height };

          // 使用最新的回调函数
          optionsRef.current.onResize?.(newDimensions);
          return newDimensions;
        }
        return prev;
      });
    }, optionsRef.current.debounceDelay);
  }, []);

  // Effect：创建和销毁ResizeObserver
  useEffect(() => {
    // 确保浏览器支持ResizeObserver
    if (typeof ResizeObserver === 'undefined') {
      console.warn('ResizeObserver is not supported in this browser');
      return;
    }

    if (containerRef.current) {
      // 创建ResizeObserver实例
      observerRef.current = new ResizeObserver(handleResize);

      // 开始观察元素
      observerRef.current.observe(containerRef.current);

      if (optionsRef.current.debug) {
        console.log('开始监控容器尺寸变化');
      }

      // 立即获取初始尺寸
      if (optionsRef.current.immediate) {
        requestAnimationFrame(() => {
          const initialDimensions = getImmediateDimensions();
          if (initialDimensions) {
            setDimensions(initialDimensions);
            optionsRef.current.onResize?.(initialDimensions);
            if (optionsRef.current.debug) {
              console.log('初始尺寸计算:', initialDimensions);
            }
          }
        });
      }
    }

    // 清理函数：组件卸载时执行
    return () => {
      // 断开观察者连接
      if (observerRef.current) {
        observerRef.current.disconnect();
        if (optionsRef.current.debug) {
          console.log('停止监控容器尺寸变化');
        }
      }

      // 清除防抖定时器
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
    };
  }, [handleResize, getImmediateDimensions]);

  // 手动刷新尺寸的函数
  const refresh = useCallback(() => {
    const newDimensions = getImmediateDimensions();
    if (newDimensions) {
      setDimensions(newDimensions);
      optionsRef.current.onResize?.(newDimensions);
    }
  }, [getImmediateDimensions]);

  // 返回容器引用和尺寸对象
  return useMemo(() => ({
    containerRef,
    width: dimensions.width,
    height: dimensions.height,
    refresh
  }), [dimensions.width, dimensions.height, refresh]);
};